import { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useAppDispatch } from 'Redux/store';
import { getBidList } from 'Redux/Bid/reducer';
import { useSelector } from 'react-redux';
import {
  selectBidList,
  selectBidCount,
  selectBidLoading,
} from 'Redux/Bid/slice';
import PageHeader from 'Components/PageHeader/PageHeader';
import { PlusOutlined } from '@ant-design/icons';
import Button from 'Components/UI/Button/Button';
import { NewLoad } from 'Components/NewLoad/NewLoad';
import { Segmented } from 'antd';
import { CustomTag } from 'Components/CustomTag/CustomTag';
import { PhoneLink } from 'Components/PhoneLink/PhoneLink';
import PageTable from 'Components/PageTable/PageTable';
import Paginations from 'Components/UI/Pagination/Pagination';
import { getUser } from 'Redux/User/reducer';
import { stringToMiles, formatDate } from 'Shared/Utils';
import { IUser } from 'Shared/Types';
import usePrevious from 'Shared/Hooks/usePrevious';
import styles from './BidsList.module.scss';

type BidType = 'Pending' | 'History';

type UserInfo = {
  [key: number]: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
  };
};

export const BidsList = () => {
  const dispatch = useAppDispatch();
  const bidList = useSelector(selectBidList);
  const bidListIsLoading = useSelector(selectBidLoading);
  const bidCount = useSelector(selectBidCount);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [bidTypeValue, setBidTypeValue] = useState<BidType>('Pending');
  const previousBidTypeValue = usePrevious(bidTypeValue);
  const [usersInfo, setUsersInfo] = useState<UserInfo>({});
  const location = useLocation();
  const baseComponentURL = '/my-bids';

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageSize = Number(params.get('page_size'));
    let page = Number(params.get('page')) || 1;

    if (bidTypeValue !== previousBidTypeValue) {
      page = 1;
    }

    dispatch(
      getBidList({ page, pageSize, isHistorical: bidTypeValue === 'History' }),
    );
  }, [location.search, bidTypeValue, dispatch]);

  useEffect(() => {
    const existingDispatcherIds = new Set<number>(
      Object.keys(usersInfo).map(Number),
    );
    const newDispatcherIds = new Set<number>();

    bidList.forEach((bid) => {
      const dispatcherId = bid?.letter?.dispatcher_id;

      if (dispatcherId && !existingDispatcherIds.has(dispatcherId)) {
        newDispatcherIds.add(dispatcherId);
      }
    });

    const newUsersInfo: UserInfo = {};

    newDispatcherIds.forEach((id) => {
      dispatch(getUser(id)).then((user) => {
        if (user.payload) {
          const { first_name, last_name, phone_number } = user.payload as IUser;

          newUsersInfo[id] = {
            firstName: first_name,
            lastName: last_name,
            phoneNumber: phone_number,
          };

          setUsersInfo((prevUsersInfo) => ({
            ...prevUsersInfo,
            ...newUsersInfo,
          }));
        }
      });
    });
  }, [bidList]);

  const handleModalVisibility = () => {
    setModalIsOpen(!modalIsOpen);
  };

  return (
    <div className={styles.bidsList}>
      <PageHeader content="My Bids">
        <Button onClick={() => setModalIsOpen(true)}>
          <PlusOutlined />
          Add load
        </Button>

        <NewLoad visible={modalIsOpen} onCancel={handleModalVisibility} />
      </PageHeader>

      <Segmented
        className={styles.bidsList__switcher}
        options={['Pending', 'History']}
        onChange={(value) => setBidTypeValue(value as BidType)}
        defaultValue={bidTypeValue}
      />

      <PageTable
        titles={[
          'Created',
          'Ref ID',
          'Pick-up and delivery',
          'Miles',
          'Unit',
          'Broker',
          'Dispatcher',
          bidTypeValue === 'History' ? 'Status' : 'Call',
        ]}
        rows={
          Boolean(bidList.length)
            ? bidList
                .filter((bid) => bid.letter)
                .map((bid) => {
                  let userInfo = {
                    firstName: '',
                    lastName: '',
                    phoneNumber: '',
                  };

                  if (usersInfo[bid.letter.dispatcher_id]) {
                    userInfo = usersInfo[bid.letter.dispatcher_id];
                  }

                  const lastCellData = bid.order_status
                    .toLowerCase()
                    .includes('await') ? (
                    <PhoneLink
                      phoneNumber={userInfo.phoneNumber}
                      title={'Make a call to the dispatcher'}
                    />
                  ) : (
                    <CustomTag
                      content={bid.assign ? 'ASSIGNED' : bid.order_status.toUpperCase()}
                    />
                  );

                  return {
                    id: bid.id,
                    expiresInMs: new Date(bid.expires).getTime(),
                    items: [
                      formatDate({
                        date: bid.created_at,
                        outputFormat: 'TIME_date',
                      }),
                      bid.order_number,
                      <>
                        <div>{bid.pick_up_at}</div>
                        <div>{bid.deliver_to}</div>
                      </>,
                      `${stringToMiles(bid.line)} mi`,
                      `$${bid.letter.broker_price || 0}`,
                      bid.broker,
                      [userInfo.firstName, userInfo.lastName].join(' '),
                      lastCellData,
                    ],
                  };
                })
            : []
        }
        baseComponentURL={'/bid'}
        isLoading={bidListIsLoading}
      />

      {bidTypeValue === previousBidTypeValue && bidList.length > 0 && (
        <Paginations
          baseComponentURL={baseComponentURL}
          totalCount={bidCount}
        />
      )}
    </div>
  );
};
